import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import "./DatePopUp.scss";
import Button from "../../ui/Button/Button";
import { IoClose, IoCalendarOutline } from "react-icons/io5";
import { formatDateRangeDisplay } from "../../../reducers/utils";

// Fallback-Daten falls API noch nicht geladen
const defaultHolidayData = {
  publicHolidays: {
    2025: [
      { date: "2025-01-01", name: "Neujahr", states: ["ALL"] },
      {
        date: "2025-01-06",
        name: "Heilige Drei Könige",
        states: ["BW", "BY", "ST"],
      },
      { date: "2025-04-18", name: "Karfreitag", states: ["ALL"] },
      { date: "2025-04-21", name: "Ostermontag", states: ["ALL"] },
      { date: "2025-05-01", name: "Tag der Arbeit", states: ["ALL"] },
      {
        date: "2025-10-03",
        name: "Tag der Deutschen Einheit",
        states: ["ALL"],
      },
      { date: "2025-12-25", name: "1. Weihnachtsfeiertag", states: ["ALL"] },
      { date: "2025-12-26", name: "2. Weihnachtsfeiertag", states: ["ALL"] },
    ],
    2026: [
      { date: "2026-01-01", name: "Neujahr", states: ["ALL"] },
      { date: "2026-04-03", name: "Karfreitag", states: ["ALL"] },
      { date: "2026-04-06", name: "Ostermontag", states: ["ALL"] },
      { date: "2026-05-01", name: "Tag der Arbeit", states: ["ALL"] },
      {
        date: "2026-10-03",
        name: "Tag der Deutschen Einheit",
        states: ["ALL"],
      },
      { date: "2026-12-25", name: "1. Weihnachtsfeiertag", states: ["ALL"] },
      { date: "2026-12-26", name: "2. Weihnachtsfeiertag", states: ["ALL"] },
    ],
  },
  schoolHolidays: {
    BE: [
      { start: "2025-02-03", end: "2025-02-08", name: "Winterferien" },
      { start: "2025-04-14", end: "2025-04-25", name: "Osterferien" },
      { start: "2025-07-24", end: "2025-09-06", name: "Sommerferien" },
      { start: "2026-02-02", end: "2026-02-07", name: "Winterferien" },
    ],
    BY: [
      { start: "2025-04-14", end: "2025-04-26", name: "Osterferien" },
      { start: "2025-08-01", end: "2025-09-15", name: "Sommerferien" },
    ],
    NW: [
      { start: "2025-04-14", end: "2025-04-26", name: "Osterferien" },
      { start: "2025-07-14", end: "2025-08-26", name: "Sommerferien" },
    ],
  },
  states: [
    { code: "BE", name: "Berlin" },
    { code: "BY", name: "Bayern" },
    { code: "NW", name: "Nordrhein-Westfalen" },
    { code: "HH", name: "Hamburg" },
    { code: "BW", name: "Baden-Württemberg" },
  ],
};

// Default-Prioritaet fuer den Wochentag-Selector, falls noch keiner gewaehlt wurde
// bzw. der bisherige nicht mehr passt: So, Sa, Fr, Do, Mi, Di, Mo.
const WEEKDAY_PRIORITY = ["Sun", "Sat", "Fri", "Thu", "Wed", "Tue", "Mon"];

const DatePopUp = ({ isOpen, onClose, onSelect, selectedValue }) => {
  const intl = useIntl();
  const modalRef = useRef(null);
  const activeMode = useSelector((state) => state.search.activeMode);
  // Hole Ferien-Daten aus Redux Store (mit Fallback auf defaults)
  const reduxHolidays = useSelector((state) => state.properties?.holidays);
  const holidayData = useMemo(() => {
    // Verwende Redux-Daten wenn vorhanden und nicht leer
    if (reduxHolidays?.states?.length > 0) {
      return reduxHolidays;
    }
    return defaultHolidayData;
  }, [reduxHolidays]);
  const today = new Date();

  const parseDate = useCallback((dateString) => {
    if (!dateString) return null;
    const parts = dateString.split(".");
    if (parts.length !== 3) return null;
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }, []);

  // Wie oft jeder Wochentag (Mon..Sun) im Zeitraum start..end tatsaechlich vorkommt -
  // Grundlage fuer die Plausibpruefung des Wochentag-Selectors (siehe isWeekdaySelectable).
  const countWeekdayOccurrences = useCallback(
    (start, end) => {
      const counts = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 };
      const startDateObj = parseDate(start);
      const endDateObj = parseDate(end);
      if (!startDateObj || !endDateObj) return counts;
      const dayMs = 24 * 60 * 60 * 1000;
      const totalDays = Math.round((endDateObj - startDateObj) / dayMs) + 1;
      if (totalDays <= 0) return counts;
      const codesByGetDay = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const current = new Date(startDateObj);
      for (let i = 0; i < totalDays; i++) {
        counts[codesByGetDay[current.getDay()]] += 1;
        current.setDate(current.getDate() + 1);
      }
      return counts;
    },
    [parseDate],
  );

  // Bei Vielfachen von 7 Tagen liegen Hin- und Rueckflug auf demselben Wochentag,
  // also braucht 1 Woche 2 Vorkommen dieses Wochentags im Zeitraum (Hin + 7 Tage
  // spaeter zurueck), 2 Wochen 3, 3 Wochen 4.
  const getRequiredWeekdayOccurrences = (dur) => (dur === 21 ? 4 : dur === 14 ? 3 : 2);

  // Nur bei den 1/2/3-Wochen-Presets ist ein Rueckflug-Wochentag ueberhaupt impliziert
  // (siehe requestHandler.js) - nur dort macht ein automatischer Default Sinn. Bei
  // jeder anderen Dauer bleibt die Auswahl leer, bis der Nutzer aktiv einen Tag waehlt.
  const isWeekMultiple = (dur) => dur === 7 || dur === 14 || dur === 21;

  const getDefaultWeekdays = useCallback(
    (start, end, dur) => {
      if (!isWeekMultiple(dur)) return [];
      const counts = countWeekdayOccurrences(start, end);
      const required = getRequiredWeekdayOccurrences(dur);
      const fallback = WEEKDAY_PRIORITY.find((code) => counts[code] >= required);
      return [fallback || "Sun"];
    },
    [countWeekdayOccurrences],
  );

  const [duration, setDuration] = useState(selectedValue?.value?.duration || 7);
  const [durationRange, setDurationRange] = useState(selectedValue?.value?.durationRange || null);
  // Originally clicked preset (range string or null). Stays put when flexibility is applied,
  // so the duration-option box keeps its visual selection while ±n expands the actual range.
  const [baseDurationRange, setBaseDurationRange] = useState(selectedValue?.value?.durationRange || null);
  // Wochentag-Auswahl fuer den Hinflug - bei jeder Dauer nutzbar (nicht nur bei
  // 1/2/3-Wochen-Presets), Mehrfachauswahl moeglich. Automatischer Default nur bei
  // Wochen-Presets (siehe isWeekMultiple), sonst leer bis der Nutzer aktiv waehlt.
  const [selectedWeekdays, setSelectedWeekdays] = useState(() =>
    selectedValue?.value?.weekdays?.length > 0
      ? selectedValue.value.weekdays
      : getDefaultWeekdays(selectedValue?.value?.start, selectedValue?.value?.end, selectedValue?.value?.duration || 7),
  );

  const getInitialMonth = () => {
    if (selectedValue?.value?.start) {
      const parts = selectedValue.value.start.split(".");
      if (parts.length === 3) {
        const startDateObj = new Date(parts[2], parts[1] - 1, parts[0]);
        if (startDateObj && !isNaN(startDateObj.getTime())) {
          return startDateObj.getMonth();
        }
      }
    }
    return today.getMonth();
  };

  const getInitialYear = () => {
    if (selectedValue?.value?.start) {
      const parts = selectedValue.value.start.split(".");
      if (parts.length === 3) {
        const startDateObj = new Date(parts[2], parts[1] - 1, parts[0]);
        if (startDateObj && !isNaN(startDateObj.getTime())) {
          return startDateObj.getFullYear();
        }
      }
    }
    return today.getFullYear();
  };

  const [currentMonth, setCurrentMonth] = useState(getInitialMonth());
  const [currentYear, setCurrentYear] = useState(getInitialYear());
  const [customDuration, setCustomDuration] = useState("");
  const [customDurationError, setCustomDurationError] = useState("");
  const [flexibility, setFlexibility] = useState(0);
  const [selectedState, setSelectedState] = useState("BE");
  const [hideNonSelectedOverlap, setHideNonSelectedOverlap] = useState(false);

  const [view, setView] = useState("calendar");

  const formatDate = (date) => {
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getDefaultStartDate = () => {
    if (selectedValue?.value?.start) {
      return selectedValue.value.start;
    }
    return "";
  };

  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(selectedValue?.value?.end || "");

  const calculateDaysBetween = useCallback(
    (start, end) => {
      if (!start || !end) return 0;
      const startDateObj = parseDate(start);
      const endDateObj = parseDate(end);
      if (!startDateObj || !endDateObj) return 0;
      const diffTime = endDateObj - startDateObj;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end day
      return diffDays > 0 ? diffDays : 0;
    },
    [parseDate],
  );

  const maxDays = calculateDaysBetween(startDate, endDate);
  const isACC = activeMode === "ACC";
  const effectiveMaxDays = isACC ? maxDays - 1 : maxDays;

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    };

    // Delay adding the listener to avoid catching the same mousedown event that opened the popup
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (selectedValue?.value) {
      setStartDate(selectedValue.value.start || getDefaultStartDate());
      setEndDate(selectedValue.value.end || "");
      const savedDuration = selectedValue.value.duration || 7;
      const savedDurationRange = selectedValue.value.durationRange || null;
      setDuration(savedDuration);
      setDurationRange(savedDurationRange);
      setSelectedWeekdays(
        selectedValue.value.weekdays?.length > 0
          ? selectedValue.value.weekdays
          : getDefaultWeekdays(selectedValue.value.start, selectedValue.value.end, savedDuration),
      );

      const presetDurations = [5, 7, 10, 14, 21];
      const presetRanges = ["6-7", "10-12"];
      setBaseDurationRange(presetRanges.includes(savedDurationRange) ? savedDurationRange : null);

      if (!presetDurations.includes(savedDuration) && !savedDurationRange) {
        setCustomDuration(savedDuration.toString());
      } else if (savedDurationRange && !presetRanges.includes(savedDurationRange)) {
        setCustomDuration(savedDurationRange);
      }

      // Update calendar month/year to match selected start date
      if (selectedValue.value.start) {
        const parts = selectedValue.value.start.split(".");
        if (parts.length === 3) {
          const startDateObj = new Date(parts[2], parts[1] - 1, parts[0]);
          if (startDateObj && !isNaN(startDateObj.getTime())) {
            setCurrentMonth(startDateObj.getMonth());
            setCurrentYear(startDateObj.getFullYear());
          }
        }
      }
    }
  }, [selectedValue]);

  useEffect(() => {
    if (startDate && endDate) {
      const days = calculateDaysBetween(startDate, endDate);
      const effDays = isACC ? days - 1 : days;
      if (effDays > 0) {
        if (durationRange) {
          const [, max] = durationRange.split("-").map(Number);
          if (max > effDays) {
            setDuration(effDays);
            setDurationRange(null);
            setCustomDuration(effDays.toString());
          }
        } else if (duration > effDays) {
          setDuration(effDays);
          setCustomDuration(effDays.toString());
        }
      }
    }
  }, [startDate, endDate, calculateDaysBetween, duration, durationRange, isACC]);

  const handleDateSelect = () => {
    const dateRange = formatDateRangeDisplay(startDate, endDate, duration, durationRange, isACC, intl);

    const isExplicitPresetClick = !customDuration && !baseDurationRange;
    const weekPreset = isACC
      ? null
      : isExplicitPresetClick && duration === 7
      ? "week"
      : isExplicitPresetClick && duration === 14
      ? "twoWeeks"
      : isExplicitPresetClick && duration === 21
      ? "threeWeeks"
      : null;

    onSelect({
      label: dateRange,
      value: {
        start: startDate,
        end: endDate,
        duration: duration,
        durationRange: durationRange,
        weekPreset,
        // Unabhaengig vom weekPreset sendbar (siehe requestHandler.js): der
        // Hinflug-Filter gilt immer, der Rueckflug-Filter nur bei weekPreset.
        weekdays: selectedWeekdays.length > 0 ? selectedWeekdays : null,
      },
    });

    handleClose();
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const isPublicHoliday = useMemo(
    () => (date) => {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const year = date.getFullYear().toString();
      const holidays = holidayData.publicHolidays[year] || [];

      return holidays.some((holiday) => {
        if (holiday.date === dateStr) {
          return holiday.states.includes("ALL") || holiday.states.includes(selectedState);
        }
        return false;
      });
    },
    [selectedState],
  );

  const isSchoolHoliday = useMemo(
    () => (date) => {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const schoolHolidays = holidayData.schoolHolidays[selectedState] || [];

      return schoolHolidays.some((holiday) => {
        return dateStr >= holiday.start && dateStr <= holiday.end;
      });
    },
    [selectedState],
  );

  // Get available school holidays for the selected state (for dropdown)
  const availableSchoolHolidays = useMemo(() => {
    const schoolHolidays = holidayData.schoolHolidays[selectedState] || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter holidays that haven't ended yet and sort by start date
    return schoolHolidays.filter((h) => new Date(h.end) >= today).sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [selectedState, holidayData]);

  // Apply selected school holiday to date range
  // Ferien-Uebernahme inkl. der angrenzenden Wochenenden UND Feiertage:
  // Reisende wollen am Wochenende VOR Ferienbeginn los und erst am Wochenende
  // NACH Ferienende zurueck — haengt direkt davor/danach ein Feiertag
  // (Brueckentag-Kette), kommt der mit rein.
  // Beispiele: Mo 19.10.–Sa 31.10. => Sa 17.10.–So 01.11.;
  // endet der Zeitraum So und Mo ist Feiertag => bis einschl. Mo.
  // Iterativ mit Guard: Wochenend-Snap (So/Mo->Sa bzw. Fr/Sa->So), dann
  // angrenzenden Feiertag einsammeln, dann ggf. erneut snappen.
  const expandToWeekends = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    for (let guard = 0; guard < 4; guard++) {
      let moved = false;
      if (s.getDay() === 1) { s.setDate(s.getDate() - 2); moved = true; }      // Mo -> Sa davor
      else if (s.getDay() === 0) { s.setDate(s.getDate() - 1); moved = true; } // So -> Sa davor
      const prev = new Date(s);
      prev.setDate(prev.getDate() - 1);
      if (isPublicHoliday(prev)) { s.setDate(s.getDate() - 1); moved = true; } // Feiertag davor mitnehmen
      if (!moved) break;
    }
    for (let guard = 0; guard < 4; guard++) {
      let moved = false;
      if (e.getDay() === 5) { e.setDate(e.getDate() + 2); moved = true; }      // Fr -> So danach
      else if (e.getDay() === 6) { e.setDate(e.getDate() + 1); moved = true; } // Sa -> So danach
      const next = new Date(e);
      next.setDate(next.getDate() + 1);
      if (isPublicHoliday(next)) { e.setDate(e.getDate() + 1); moved = true; } // Feiertag danach mitnehmen
      if (!moved) break;
    }
    return [s, e];
  };

  const applySchoolHoliday = (holidayIndex) => {
    if (holidayIndex === "" || holidayIndex === null) return;

    const holiday = availableSchoolHolidays[parseInt(holidayIndex)];
    if (!holiday) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Parse dates
    const startParts = holiday.start.split("-");
    const endParts = holiday.end.split("-");

    const holidayStart = new Date(startParts[0], startParts[1] - 1, startParts[2]);
    const holidayEnd = new Date(endParts[0], endParts[1] - 1, endParts[2]);

    // Angrenzende Wochenenden komplett in den Reisezeitraum nehmen
    const [expandedStart, expandedEnd] = expandToWeekends(holidayStart, holidayEnd);

    // If holiday has already started, use today as start
    const effectiveStart = expandedStart < today ? today : expandedStart;

    // Format dates for state
    const formattedStart = formatDate(effectiveStart);
    const formattedEnd = formatDate(expandedEnd);

    setStartDate(formattedStart);
    setEndDate(formattedEnd);

    // Navigate calendar to show the holiday
    setCurrentMonth(effectiveStart.getMonth());
    setCurrentYear(effectiveStart.getFullYear());
  };

  // Snap to school holidays visible in current calendar view
  const snapToSchoolHolidays = () => {
    // Get the date range currently visible in the calendar (2 months)
    const calendarStart = new Date(currentYear, currentMonth, 1);
    const calendarEnd = new Date(currentYear, currentMonth + 2, 0); // Last day of second month

    const schoolHolidays = holidayData.schoolHolidays[selectedState] || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Find the first school holiday that overlaps with the visible calendar
    for (const holiday of schoolHolidays) {
      const startParts = holiday.start.split("-");
      const endParts = holiday.end.split("-");

      const holidayStart = new Date(startParts[0], startParts[1] - 1, startParts[2]);
      const holidayEnd = new Date(endParts[0], endParts[1] - 1, endParts[2]);

      // Check if holiday overlaps with visible calendar and hasn't ended
      if (holidayEnd >= today && holidayStart <= calendarEnd && holidayEnd >= calendarStart) {
        // Angrenzende Wochenenden komplett in den Reisezeitraum nehmen
        const [expandedStart, expandedEnd] = expandToWeekends(holidayStart, holidayEnd);

        // Use today if holiday has already started
        const effectiveStart = expandedStart < today ? today : expandedStart;

        setStartDate(formatDate(effectiveStart));
        setEndDate(formatDate(expandedEnd));
        return;
      }
    }

    // No visible holidays found - try to find next upcoming holiday
    if (availableSchoolHolidays.length > 0) {
      applySchoolHoliday(0);
    }
  };

  const generateCalendarDays = (monthOffset = 0) => {
    const month = currentMonth + monthOffset;
    const year = currentYear + Math.floor(month / 12);
    const adjustedMonth = month % 12;

    const firstDay = new Date(year, adjustedMonth, 1);
    const lastDay = new Date(year, adjustedMonth + 1, 0);

    // Calculate position of first day (0 = Monday, 6 = Sunday)
    const dayOfWeek = firstDay.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    // Dynamic row calculation: ceil((position + daysInMonth) / 7)
    const daysInMonth = lastDay.getDate();
    const requiredRows = Math.ceil((daysFromMonday + daysInMonth) / 7);
    const totalDays = requiredRows * 7;

    const startDay = new Date(firstDay);
    startDay.setDate(startDay.getDate() - daysFromMonday);

    const days = [];
    const current = new Date(startDay);

    for (let i = 0; i < totalDays; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDayClick = (date) => {
    if (isDateDisabled(date)) return;

    const formattedDate = formatDate(date);

    if (!startDate || (startDate && endDate)) {
      setStartDate(formattedDate);
      setEndDate("");
    } else if (startDate && !endDate) {
      const startDateObj = parseDate(startDate);
      if (date >= startDateObj) {
        setEndDate(formattedDate);
      } else {
        setStartDate(formattedDate);
        setEndDate("");
      }
    }
  };

  const navigateMonth = (direction) => {
    if (direction === "prev") {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const getMonthNames = () => [
    intl.formatMessage({ id: "january" }),
    intl.formatMessage({ id: "february" }),
    intl.formatMessage({ id: "march" }),
    intl.formatMessage({ id: "april" }),
    intl.formatMessage({ id: "may" }),
    intl.formatMessage({ id: "june" }),
    intl.formatMessage({ id: "july" }),
    intl.formatMessage({ id: "august" }),
    intl.formatMessage({ id: "september" }),
    intl.formatMessage({ id: "october" }),
    intl.formatMessage({ id: "november" }),
    intl.formatMessage({ id: "december" }),
  ];

  const getWeekdays = () => [
    intl.formatMessage({ id: "monday" }),
    intl.formatMessage({ id: "tuesday" }),
    intl.formatMessage({ id: "wednesday" }),
    intl.formatMessage({ id: "thursday" }),
    intl.formatMessage({ id: "friday" }),
    intl.formatMessage({ id: "saturday" }),
    intl.formatMessage({ id: "sunday" }),
  ];

  const monthNames = getMonthNames();
  const weekdays = getWeekdays();

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    const startDateObj = parseDate(startDate);
    const endDateObj = parseDate(endDate);
    return date >= startDateObj && date <= endDateObj;
  };

  const handleDateInput = (value, type) => {
    const dateRegex = /^\d{2}\.\d{2}\.\d{4}$/;
    if (dateRegex.test(value)) {
      const parsedDate = parseDate(value);
      if (parsedDate && !isDateDisabled(parsedDate)) {
        setCurrentMonth(parsedDate.getMonth());
        setCurrentYear(parsedDate.getFullYear());

        if (type === "start") {
          setStartDate(value);
          if (endDate) {
            const endDateObj = parseDate(endDate);
            if (parsedDate > endDateObj) {
              setEndDate("");
            }
          }
        } else {
          const startDateObj = parseDate(startDate);
          if (parsedDate >= startDateObj) {
            setEndDate(value);
          }
        }
      }
    } else {
      if (type === "start") {
        setStartDate(value);
      } else {
        setEndDate(value);
      }
    }
  };

  const handleCustomDurationChange = (value) => {
    setCustomDuration(value);
    setCustomDurationError("");
    setBaseDurationRange(null);

    if (!value.trim()) {
      setDurationRange(null);
      return;
    }

    const rangeMatch = value.match(/^(\d+)-(\d+)$/);

    if (rangeMatch) {
      const min = parseInt(rangeMatch[1], 10);
      const max = parseInt(rangeMatch[2], 10);

      if (min >= max) {
        setCustomDurationError(intl.formatMessage({ id: "durationRangeError" }) || "Erste Zahl muss kleiner sein als die zweite");
        setDurationRange(null);
        return;
      }

      if (effectiveMaxDays > 0 && max > effectiveMaxDays) {
        setCustomDurationError(intl.formatMessage({ id: "durationExceedsMaxError" }, { max: effectiveMaxDays }) || `Die Dauer darf nicht mehr als ${effectiveMaxDays} Tage betragen`);
        setDurationRange(null);
        return;
      }

      if (min <= 0 || max <= 0) {
        setCustomDurationError(intl.formatMessage({ id: "durationPositiveError" }) || "Die Anzahl der Tage muss positiv sein");
        setDurationRange(null);
        return;
      }

      setDuration(min);
      setDurationRange(value);
    } else {
      const numValue = parseInt(value, 10);

      if (!isNaN(numValue) && numValue > 0) {
        if (effectiveMaxDays > 0 && numValue > effectiveMaxDays) {
          setCustomDurationError(intl.formatMessage({ id: "durationExceedsMaxError" }, { max: effectiveMaxDays }) || `Die Dauer darf nicht mehr als ${effectiveMaxDays} Tage betragen`);
          setDuration(effectiveMaxDays);
          setCustomDuration(effectiveMaxDays.toString());
          setDurationRange(null);
        } else {
          setDuration(numValue);
          setDurationRange(null);
        }
      } else {
        setCustomDurationError(intl.formatMessage({ id: "durationInvalidError" }) || "Bitte geben Sie eine gültige Anzahl von Tagen ein");
        setDurationRange(null);
      }
    }
  };

  const handleDurationSelect = (days, range = null) => {
    if (range) {
      const [min] = range.split("-").map(Number);
      setDuration(min);
      setDurationRange(range);
      setBaseDurationRange(range);
    } else {
      setDuration(days);
      setDurationRange(null);
      setBaseDurationRange(null);
    }
    setCustomDuration("");
    setCustomDurationError("");
    setFlexibility(0);
  };

  const toggleWeekday = (day) => {
    setSelectedWeekdays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const isExact = effectiveMaxDays > 0 && duration === effectiveMaxDays && !baseDurationRange && !customDuration;


  const weekdayOccurrenceCounts = useMemo(() => countWeekdayOccurrences(startDate, endDate), [startDate, endDate, countWeekdayOccurrences]);
  const requiredWeekdayOccurrences = getRequiredWeekdayOccurrences(duration);

  const isWeekdaySelectable = (code) => {
    if (!startDate || !endDate) return true;
    return (weekdayOccurrenceCounts[code] || 0) >= requiredWeekdayOccurrences;
  };

  // Wenn sich Zeitraum oder Dauer aendern und die bisherige Auswahl dadurch nicht mehr
  // plausibel ist (Wochentag passt nicht mehr oft genug in den Zeitraum): bei den
  // 1/2/3-Wochen-Presets auf die Prioritaet So>Sa>Fr>Do>Mi>Di>Mo zurueckfallen (dort
  // gibt es immer einen sinnvollen Default), bei jeder anderen Dauer einfach leeren
  // statt einen Tag aufzuzwingen.
  useEffect(() => {
    if (isACC || !startDate || !endDate) return;
    setSelectedWeekdays((prev) => {
      const stillValid = prev.filter((code) => (weekdayOccurrenceCounts[code] || 0) >= requiredWeekdayOccurrences);
      if (stillValid.length > 0) return stillValid;
      if (!isWeekMultiple(duration)) return [];
      const fallback = WEEKDAY_PRIORITY.find((code) => (weekdayOccurrenceCounts[code] || 0) >= requiredWeekdayOccurrences);
      return fallback ? [fallback] : prev;
    });
  }, [isACC, duration, startDate, endDate, requiredWeekdayOccurrences, weekdayOccurrenceCounts]);

  const weekdayOptions = [
    { code: "Mon", labelId: "monday" },
    { code: "Tue", labelId: "tuesday" },
    { code: "Wed", labelId: "wednesday" },
    { code: "Thu", labelId: "thursday" },
    { code: "Fri", labelId: "friday" },
    { code: "Sat", labelId: "saturday" },
    { code: "Sun", labelId: "sunday" },
  ];

  const handleExactToggle = () => {
    if (isExact) {
      setDuration(7);
    } else if (effectiveMaxDays > 0) {
      setDuration(effectiveMaxDays);
      setDurationRange(null);
      setBaseDurationRange(null);
      setCustomDuration("");
      setCustomDurationError("");
      setFlexibility(0);
    }
  };

  const handleFlexSelect = (flex) => {
    const newFlex = flexibility === flex ? 0 : flex;
    setFlexibility(newFlex);
    const baseDuration = customDuration ? parseInt(customDuration, 10) : duration;
    if (newFlex === 0) {
      setDurationRange(null);
    } else if (baseDuration > 0) {
      const min = Math.max(1, baseDuration - newFlex);
      const max = effectiveMaxDays > 0 ? Math.min(baseDuration + newFlex, effectiveMaxDays) : baseDuration + newFlex;
      setDurationRange(`${min}-${max}`);
    }
  };

  const shouldShowDurationOption = (days, range = null) => {
    if (!startDate || !endDate) return true;
    if (range) {
      const [, max] = range.split("-").map(Number);
      return effectiveMaxDays >= max;
    }
    return effectiveMaxDays >= days;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="date-popup-overlay">
      <div className="date-popup-modal" ref={modalRef}>
        <div className="popup-header">
          <div className="popup-header-text">
            <span className="popup-title">{intl.formatMessage({ id: "travelPeriod" })}</span>
          </div>
          <button className="popup-close-button" onClick={handleClose}>
            <IoClose />
          </button>
        </div>

        <div className="view-tabs">
          <div className={`view-tab ${view === "calendar" ? "active" : ""}`} onClick={() => setView("calendar")}>
            {intl.formatMessage({ id: "calendar" })}
          </div>
          <div className={`view-tab ${view === "duration" ? "active" : ""}`} onClick={() => setView("duration")}>
            {intl.formatMessage({ id: "duration" })}
          </div>
        </div>

        <div className="popup-content">
          {view === "calendar" && (
            <div className="date-picker">
              <div className="calendar-header">
                <button className="nav-button" onClick={() => navigateMonth("prev")} type="button">
                  ‹
                </button>
                <div className="month-headers">
                  <span className="month-year">
                    {monthNames[currentMonth]} {currentYear}
                  </span>
                  <span className="separator">|</span>
                  <span className="month-year">
                    {monthNames[(currentMonth + 1) % 12]} {currentMonth === 11 ? currentYear + 1 : currentYear}
                  </span>
                </div>
                <button className="nav-button" onClick={() => navigateMonth("next")} type="button">
                  ›
                </button>
              </div>

              <div className="holiday-filter">
                <label>{intl.formatMessage({ id: "selectState" }) || "Bundesland auswählen"}</label>
                <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)}>
                  {holidayData.states.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
                <div className="legend">
                  <div
                    className="legend-item legend-toggle"
                    onClick={() => setHideNonSelectedOverlap(!hideNonSelectedOverlap)}
                    title={hideNonSelectedOverlap ? "Überlappungen anzeigen" : "Überlappungen ausblenden"}
                  >
                    <span className="legend-triangle holiday-triangle" />
                    <span>{intl.formatMessage({ id: "publicHolidays" })}</span>
                  </div>
                  <div className="legend-item school-holidays-item">
                    <span className="legend-triangle school-triangle" />
                    {availableSchoolHolidays.length > 0 ? (
                      <select className="school-holiday-dropdown" value="" onChange={(e) => applySchoolHoliday(e.target.value)}>
                        <option value="" disabled>
                          {intl.formatMessage({ id: "schoolHolidays" })}
                        </option>
                        {availableSchoolHolidays.map((holiday, index) => (
                          <option key={index} value={index}>
                            {holiday.name}
                            {holiday.start ? ` (${holiday.start.slice(2, 4)})` : ""}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{intl.formatMessage({ id: "schoolHolidays" })}</span>
                    )}
                  </div>
                  <button className="snap-to-holidays-btn" onClick={snapToSchoolHolidays} title={intl.formatMessage({ id: "snapToHolidays" })}>
                    <IoCalendarOutline />
                    <span>{intl.formatMessage({ id: "snapToHolidays" })}</span>
                  </button>
                </div>
              </div>

              <div className="dual-calendar-container">
                <div className="calendar-grid">
                  <div className="weekdays">
                    {weekdays.map((day) => (
                      <div key={day} className="weekday">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="days">
                    {generateCalendarDays(0).map((date, index) => {
                      const formattedDate = formatDate(date);
                      const isStart = formattedDate === startDate;
                      const isEnd = formattedDate === endDate;
                      const isInRange = isDateInRange(date);
                      const isDisabled = isDateDisabled(date);
                      const isCurrentMonth = date.getMonth() === currentMonth;
                      const isPubHoliday = isPublicHoliday(date);
                      const isSchHoliday = isSchoolHoliday(date);
                      // Left calendar: Hide NEXT month days (overlap with right calendar), keep PREVIOUS month visible
                      const isNextMonth = date.getFullYear() > currentYear || (date.getFullYear() === currentYear && date.getMonth() > currentMonth);
                      const shouldHide = hideNonSelectedOverlap && !isCurrentMonth && isNextMonth;

                      return (
                        <div
                          key={index}
                          className={`day ${!isCurrentMonth ? "other-month" : ""} ${isStart ? "selected start" : ""} ${isEnd ? "selected end" : ""} ${
                            isInRange && !isStart && !isEnd ? "in-range" : ""
                          } ${isDisabled ? "disabled" : ""} ${shouldHide ? "hidden-day" : ""}`}
                          onClick={() => !isDisabled && !shouldHide && handleDayClick(date)}
                          title={shouldHide ? "" : isPubHoliday && isSchHoliday ? "Feiertag + Schulferien" : isPubHoliday ? "Feiertag" : isSchHoliday ? "Schulferien" : ""}
                        >
                          {!shouldHide && (
                            <>
                              <span className="day-number">{date.getDate()}</span>
                              {isPubHoliday && <span className="corner-triangle holiday-triangle" />}
                              {isSchHoliday && <span className="corner-triangle school-triangle" />}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="calendar-grid">
                  <div className="weekdays">
                    {weekdays.map((day) => (
                      <div key={`${day}-2`} className="weekday">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="days">
                    {generateCalendarDays(1).map((date, index) => {
                      const formattedDate = formatDate(date);
                      const isStart = formattedDate === startDate;
                      const isEnd = formattedDate === endDate;
                      const isInRange = isDateInRange(date);
                      const isDisabled = isDateDisabled(date);
                      const nextMonth = (currentMonth + 1) % 12;
                      const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
                      const isCurrentMonth = date.getMonth() === nextMonth && date.getFullYear() === nextYear;
                      // Check if day belongs to previous month (should hide) vs next month (keep visible)
                      const isPreviousMonth = date.getFullYear() < nextYear || (date.getFullYear() === nextYear && date.getMonth() < nextMonth);
                      const isPubHoliday = isPublicHoliday(date);
                      const isSchHoliday = isSchoolHoliday(date);
                      // Only hide previous month days, keep next month days visible
                      const shouldHide = hideNonSelectedOverlap && !isCurrentMonth && isPreviousMonth;

                      return (
                        <div
                          key={index}
                          className={`day ${!isCurrentMonth ? "other-month" : ""} ${isStart ? "selected start" : ""} ${isEnd ? "selected end" : ""} ${
                            isInRange && !isStart && !isEnd ? "in-range" : ""
                          } ${isDisabled ? "disabled" : ""} ${shouldHide ? "hidden-day" : ""}`}
                          onClick={() => !isDisabled && !shouldHide && handleDayClick(date)}
                          title={shouldHide ? "" : isPubHoliday && isSchHoliday ? "Feiertag + Schulferien" : isPubHoliday ? "Feiertag" : isSchHoliday ? "Schulferien" : ""}
                        >
                          {!shouldHide && (
                            <>
                              <span className="day-number">{date.getDate()}</span>
                              {isPubHoliday && <span className="corner-triangle holiday-triangle" />}
                              {isSchHoliday && <span className="corner-triangle school-triangle" />}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="date-inputs">
                <div className="date-input-group">
                  <label>{intl.formatMessage({ id: "arrival" })}</label>
                  <input type="text" value={startDate} onChange={(e) => handleDateInput(e.target.value, "start")} placeholder="DD.MM.YYYY" />
                </div>
                <div className="date-input-group">
                  <label>{intl.formatMessage({ id: "departure" })}</label>
                  <input type="text" value={endDate} onChange={(e) => handleDateInput(e.target.value, "end")} placeholder="DD.MM.YYYY" />
                </div>
              </div>

              <Button text={intl.formatMessage({ id: "next" })} style={"rounded"} color={"orange"} size={"m"} onClick={() => setView("duration")} />
            </div>
          )}

          {view === "duration" && (
            <div className="duration-selector">
              <div className="duration-header">
                <h4>{intl.formatMessage({ id: "selectDuration" })}</h4>
              </div>
              <div className="duration-options">
                {effectiveMaxDays > 0 && (
                  <div className={`duration-option exact-option ${isExact ? "selected" : ""}`} onClick={handleExactToggle}>
                    <span className="duration-value">{intl.formatMessage({ id: "exactAsEntered", defaultMessage: "Exakt wie eingegeben" })}</span>
                    <span className="duration-label">
                      {effectiveMaxDays} {intl.formatMessage({ id: isACC ? "overnightStays" : "days" })}
                    </span>
                  </div>
                )}
                {shouldShowDurationOption(5) && (
                  <div className={`duration-option ${duration === 5 && !customDuration && !baseDurationRange ? "selected" : ""}`} onClick={() => handleDurationSelect(5)}>
                    <span className="duration-value">5</span>
                    <span className="duration-label">{intl.formatMessage({ id: isACC ? "overnightStays" : "days" })}</span>
                  </div>
                )}
                {shouldShowDurationOption(7, "6-7") && (
                  <div className={`duration-option ${baseDurationRange === "6-7" && !customDuration ? "selected" : ""}`} onClick={() => handleDurationSelect(6, "6-7")}>
                    <span className="duration-value">6-7</span>
                    <span className="duration-label">{intl.formatMessage({ id: isACC ? "overnightStays" : "days" })}</span>
                  </div>
                )}
                {shouldShowDurationOption(7) && (
                  <div className={`duration-option ${duration === 7 && !customDuration && !baseDurationRange ? "selected" : ""}`} onClick={() => handleDurationSelect(7)}>
                    <span className="duration-value">1</span>
                    <span className="duration-label">{intl.formatMessage({ id: "week" })}</span>
                  </div>
                )}
                {shouldShowDurationOption(10) && (
                  <div className={`duration-option ${duration === 10 && !customDuration && !baseDurationRange ? "selected" : ""}`} onClick={() => handleDurationSelect(10)}>
                    <span className="duration-value">10</span>
                    <span className="duration-label">{intl.formatMessage({ id: isACC ? "overnightStays" : "days" })}</span>
                  </div>
                )}
                {shouldShowDurationOption(12, "10-12") && (
                  <div className={`duration-option ${baseDurationRange === "10-12" && !customDuration ? "selected" : ""}`} onClick={() => handleDurationSelect(10, "10-12")}>
                    <span className="duration-value">10-12</span>
                    <span className="duration-label">{intl.formatMessage({ id: isACC ? "overnightStays" : "days" })}</span>
                  </div>
                )}
                {shouldShowDurationOption(14) && (
                  <div className={`duration-option ${duration === 14 && !customDuration && !baseDurationRange ? "selected" : ""}`} onClick={() => handleDurationSelect(14)}>
                    <span className="duration-value">2</span>
                    <span className="duration-label">{intl.formatMessage({ id: "weeks" })}</span>
                  </div>
                )}
                {shouldShowDurationOption(21) && (
                  <div className={`duration-option ${duration === 21 && !customDuration && !baseDurationRange ? "selected" : ""}`} onClick={() => handleDurationSelect(21)}>
                    <span className="duration-value">3</span>
                    <span className="duration-label">{intl.formatMessage({ id: "weeks" })}</span>
                  </div>
                )}
              </div>

              {!isACC && (
                <div className="weekday-selector">
                  <label>{intl.formatMessage({ id: "outboundWeekdays" })}</label>
                  <div className="weekday-selector-options">
                    {weekdayOptions.map((w) => {
                      const selectable = isWeekdaySelectable(w.code);
                      return (
                        <div
                          key={w.code}
                          className={`weekday-option ${selectedWeekdays.includes(w.code) ? "selected" : ""} ${!selectable ? "disabled" : ""}`}
                          onClick={() => selectable && toggleWeekday(w.code)}
                          title={!selectable ? intl.formatMessage({ id: "weekdayNotAvailable" }) || "" : ""}
                        >
                          {intl.formatMessage({ id: w.labelId }).slice(0, 2)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="custom-duration-input">
                <div className="custom-duration-row">
                  <div className="custom-duration-field">
                    <label>{intl.formatMessage({ id: isACC ? "customDurationNights" : "customDuration" })}</label>
                    <input
                      type="text"
                      value={customDuration}
                      onChange={(e) => handleCustomDurationChange(e.target.value)}
                      placeholder={intl.formatMessage({ id: "durationPlaceholder" }) || "z.B. 7 oder 6-7"}
                      className={customDurationError ? "error" : ""}
                    />
                  </div>
                  <div className="flex-field">
                    <label>{intl.formatMessage({ id: "flexibility" })}</label>
                    <div className="flex-btns">
                      {[1, 2, 3].map((n) => (
                        <div key={n} className={`flex-btn ${flexibility === n ? "selected" : ""}`} onClick={() => handleFlexSelect(n)}>
                          ±{n}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {customDurationError && <span className="duration-error-message">{customDurationError}</span>}
                {effectiveMaxDays > 0 && !customDurationError && (
                  <span className="max-days-hint">
                    {intl.formatMessage({ id: "maxDays" })}: {effectiveMaxDays} {intl.formatMessage({ id: isACC ? "overnightStays" : "days" })}
                  </span>
                )}
              </div>

              <Button text={intl.formatMessage({ id: "apply" })} style={"rounded"} color={"orange"} size={"m"} onClick={() => handleDateSelect()} disabled={!!customDurationError} />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DatePopUp;
